/**
 * The desktop layout — a faithful port of `Portfolio Tracker.dc.html`, with the
 * prototype's localStorage swapped for the API-backed store and two panels the
 * prototype had no need for (account, sync state).
 */

import { h } from '../vdom.js';
import {
  C, MONO, SANS, css, card, kicker, bar, input, select, button, removeButton, tap,
  alertText, incomeLabel, expenseLabel, expenseText, classLabel, carTypeLabel, gainColor, emptyLine,
  netColor, netBarFill, barFigure,
  vehicleForm, currencyPicker, monthPicker, datePicker, pageClass, holdingExitDialog, recurringAgainDialog,
  editDialog, costLine, basisPreview, upcomingPanel, adSlot,
  reviewPanel, termsNotice,
  fullTankToggle, demoBanner, debtChange, quotesAllowed, planLine,
  logoMark, isPremium,
  guideLink
} from './common.js';
import { whatIfPanel } from './whatif.js';
import { cashKpis, flowCard, transactionsCard, fixedCard } from './cashflow.js';
import { debtKpis, debtChart, debtQueue, debtPlanCard, debtPlans } from './debts.js';
import { investTab } from './investments.js';
import { vehicleTab } from './vehicle.js';
import {
  kpiRow, trendCard, moneyCard, categoriesCard, attentionCard, upcomingCard, reviewCard, whatIfCard, simulatorPage
} from './overview.js';
import {
  CATEGORIES, ASSET_CLASSES, CAR_EXPENSE_TYPES, isAmountOnly
} from '../../../shared/model.js';
import { renderJournal } from './journal.js';
import { renderTaxes } from './taxes.js';
import { budgetsCard, goalsCard } from './goals.js';

const TABS = ['overview', 'cash', 'debt', 'invest', 'car', 'journal', 'tax'];

export function renderDesktop(ctx) {
  const { state, t } = ctx;
  return h('div', {
    key: 'desktop',
    style: css({
      display: 'flex',
      minHeight: '100vh',
      width: '100%',
      background: C.bg,
      color: C.text,
      fontFamily: SANS,
      fontSize: '14px',
      WebkitFontSmoothing: 'antialiased'
    })
  },
    sidebar(ctx),
    /*
     * `overflow-x: clip` because the page below animates in from the side.
     *
     * A forward transition starts the tab body 14px right of where it lands, so
     * for a quarter of a second the document was 14px wider than the viewport
     * and a horizontal scrollbar flashed in along the bottom right corner —
     * measured, not guessed: scrollWidth went 1280 → 1284 mid-animation.
     *
     * `clip` rather than `hidden`: hidden would make this a scroll container
     * (overflow-y computes to auto once the other axis is not visible), moving
     * the page's scrolling inside this column and changing how the sticky
     * sidebar behaves. `clip` clips and nothing else. The one absolutely
     * positioned thing in here — the holdings month picker — sits 514px clear
     * of this edge, so nothing of it is lost.
     */
    h('div', {
      key: 'main',
      style: css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflowX: 'clip' })
    },
      header(ctx),
      /*
       * Keyed by tab, not just "the body". One of five renderers occupies this
       * slot at a time and a nav click is what swaps them; with a constant key
       * the outgoing tab's elements are patched into the incoming tab's and
       * inherit its handlers while that click is still bubbling. It also means
       * a new tab opens at the top, which is what you expect of a new tab.
       */
      h('div', {
        key: 'tab-' + state.tab,
        class: pageClass(state),
        style: css({ flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', gap: '18px' })
      },
        state.user?.isGuest && demoBanner(ctx),
        state.tab === 'overview' && overview(ctx),
        state.tab === 'cash' && cashflow(ctx),
        state.tab === 'debt' && debtTab(ctx),
        state.tab === 'invest' && investments(ctx),
        state.tab === 'car' && vehicle(ctx),
        state.tab === 'journal' && renderJournal(ctx),
        state.tab === 'tax' && renderTaxes(ctx)
      )
    ),
    /* Sits above everything; only renders while a position is being closed. */
    state.holdingExit && holdingExitDialog(ctx),
    state.recurringAgain && recurringAgainDialog(ctx),
    /* One row at a time, whichever kind it is. */
    state.editing && editDialog(ctx)
  );
}

// --------------------------------------------------------------- sidebar ----

function sidebar(ctx) {
  const { state, metrics, t, fmt, actions } = ctx;

  const navItems = TABS.map(tab => ({
    tab,
    label: t.nav[tab],
    badge: tab === 'overview' ? '' : String(metrics.counts[tab] ?? '')
  }));

  return h('div', {
    key: 'sidebar',
    style: css({
      width: '224px',
      flex: '0 0 224px',
      background: C.panel,
      borderRight: `1px solid ${C.line}`,
      display: 'flex',
      flexDirection: 'column',
      position: 'sticky',
      top: 0,
      height: '100vh'
    })
  },
    h('div', {
      style: css({
        height: '74px', flex: '0 0 74px', display: 'flex', alignItems: 'center',
        gap: '10px', padding: '0 16px', borderBottom: `1px solid ${C.line}`
      })
    },
      logoMark(28, { premium: isPremium(state), ring: C.panel }),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', minWidth: 0 }) },
        h('div', { style: brandLine }, 'PORTFOLIO'),
        h('div', { style: brandLine }, 'TRACKER',
          isPremium(state) && h('span', { key: 'plus', style: css({ color: C.gold }) }, '+')),
        /*
         * Liquidity+ needs no caption: the gold "+" on the wordmark and the seal
         * on the shield say it, and without a third line the wordmark sits
         * centred against the logo.
         */
        !isPremium(state) && h('div', {
          key: 'kicker',
          style: css({
            fontFamily: MONO, fontSize: '7.5px', letterSpacing: '1.5px',
            color: C.accent, lineHeight: 1, marginTop: '5px'
          })
        }, t.brandKicker)
      )
    ),

    h('div', { style: css({ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }) },
      kicker(t.desk, { padding: '16px 16px 6px', fontSize: '9px', letterSpacing: '1.4px' }),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '2px', padding: '0 8px' }) },
        ...navItems.map(item => h('div', {
          key: item.tab,
          ...tap(() => actions.setTab(item.tab)),
          'aria-current': state.tab === item.tab ? 'page' : null,
          class: 'pt-nav',
          style: css({
            display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 10px',
            borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 500,
            background: state.tab === item.tab ? C.line : 'transparent',
            color: state.tab === item.tab ? C.text : C.muted
          })
        },
          h('div', {
            style: css({
              width: '6px', height: '6px', borderRadius: '2px', flex: '0 0 6px',
              background: state.tab === item.tab ? C.accent : C.strokeHi
            })
          }),
          h('div', {
            style: css({ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' })
          }, item.label),
          h('div', { style: css({ fontFamily: MONO, fontSize: '10px', color: C.faint }) }, item.badge)
        ))
      )
    ),

    sidebarFoot(ctx)
  );
}

const brandLine = css({
  fontSize: '16px', fontWeight: 800, fontStyle: 'italic',
  letterSpacing: '-0.6px', lineHeight: '.96', whiteSpace: 'nowrap'
});

/**
 * The foot of the sidebar, as the "Preferencje v2" design draws it: the guide,
 * then one card for the account -- who is signed in, whether the ledger is
 * synced, which plan -- with a ⚙ straight into Preferences and, on the card
 * itself, a menu for the rest (Preferences, the admin panel, signing out).
 *
 * The language switch and the sync box that used to sit here went into that
 * card and into Preferences → General. What could not wait for a menu stays
 * out in the open above it: a missing exchange rate, an unconfirmed address,
 * and a demo's way to become an account.
 */
function sidebarFoot(ctx) {
  const { state, metrics, t, actions } = ctx;
  const s = t.shell;
  const user = state.user || {};
  const busy = state.syncVisible;
  const dot = !state.online ? C.danger : busy ? C.accentHi : C.accent;
  const name = user.isGuest ? s.guest : (user.discordHandle || user.email || user.displayName || '—');
  const initial = (user.discordHandle || user.email || user.displayName || '?').replace(/^@/, '').charAt(0).toUpperCase();
  /* A demo is neither: it shows everything, and it is not anybody's plan. */
  const planName = user.isGuest ? 'Demo' : isPremium(state) ? 'Liquidity+' : 'Free';
  /*
   * Reads `syncVisible`, not `syncing`/`pending`: a queued settings change
   * flushes in about forty milliseconds, and announcing that made the line
   * flicker on every click. The store holds it until something is outstanding.
   */
  const line = !state.online ? s.prefCardOffline(state.pending || 0)
    : busy ? s.prefCardSyncing
      : s.prefCardLine(metrics.totals.entryCount, planName);
  const alertBox = (key, onClick, heading, body, extra = null) => h('div', {
    key,
    ...(onClick ? tap(onClick) : {}),
    style: css({
      display: 'flex', flexDirection: 'column', gap: '6px', padding: '9px 10px',
      border: `1px solid ${C.danger}`, borderRadius: '8px', background: C.card, cursor: onClick ? 'pointer' : 'default'
    })
  },
    h('div', { style: css({ fontSize: '11px', fontWeight: 600, color: C.danger }) }, heading),
    h('div', { style: css({ fontSize: '10.5px', lineHeight: 1.45, color: C.muted }) }, body),
    extra
  );
  const menuItem = (key, icon, label, hint, onClick, color, iconColor) => h('div', {
    key,
    ...tap(() => { actions.toggleAccountMenu(false); onClick(); }, label),
    role: 'menuitem',
    class: 'pt-nav',
    style: css({ minHeight: '40px', display: 'flex', alignItems: 'center', gap: '10px', padding: '0 10px', borderRadius: '8px', fontSize: '13px', color, cursor: 'pointer' })
  },
    h('span', { style: css({ width: '16px', textAlign: 'center', fontSize: '13px', color: iconColor }) }, icon),
    h('span', { style: css({ flex: 1 }) }, label),
    hint && h('span', { key: 'hint', style: css({ fontFamily: MONO, fontSize: '10.5px', color: C.faint }) }, hint)
  );

  return h('div', {
    key: 'foot',
    style: css({ flex: '0 0 auto', padding: '10px', borderTop: `1px solid ${C.line}`, display: 'flex', flexDirection: 'column', gap: '8px', position: 'relative' })
  },
    guideLink(ctx, {
      display: 'flex', alignItems: 'center', gap: '10px', minHeight: '40px', padding: '0 10px', borderRadius: '8px',
      fontSize: '13px', color: C.textDim
    },
      h('span', {
        style: css({
          width: '20px', height: '20px', flex: '0 0 20px', borderRadius: '50%', border: `1px solid ${C.strokeHi}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: C.muted
        })
      }, '?'),
      h('span', { style: css({ flex: 1 }) }, s.guideLink),
      h('span', { style: css({ fontSize: '11px', color: C.faint }) }, '↗')),

    /*
     * A missing rate silently shortens every total, so it gets the same weight
     * as an unconfirmed address: named, in red, with the fix one click away.
     */
    metrics.missingRates.length > 0 && alertBox('acc-rates', () => actions.openSettings('currencies'),
      s.missingRateTitle, s.missingRateBody(metrics.missingRates.join(', '))),
    // An unconfirmed address is not an error, but it should not be quiet either.
    user.email && user.emailVerified === false && alertBox('acc-verify', null, s.verifyBannerTitle, s.verifyBannerBody,
      button(s.verifyResend, actions.resendVerification, { tone: 'muted', style: { fontSize: '9px', padding: '6px 8px' } })),
    user.isGuest && h('div', { key: 'acc-claim' }, button(s.claim, actions.openClaim, {
      tone: 'accent', style: { fontSize: '9.5px', padding: '9px 8px', borderRadius: '8px' }
    })),
    busy && state.pending > 0 && h('div', {
      key: 'acc-pending', style: css({ fontFamily: MONO, fontSize: '9.5px', color: C.accentHi, lineHeight: 1.4, padding: '0 4px' })
    }, s.pendingChanges(state.pending)),

    state.accountMenuOpen && h('div', {
      key: 'menu-catcher',
      onclick: () => actions.toggleAccountMenu(false),
      style: css({ position: 'fixed', inset: 0, zIndex: 29 })
    }),
    state.accountMenuOpen && h('div', {
      key: 'menu',
      role: 'menu',
      style: css({
        position: 'absolute', left: '10px', right: '10px', bottom: '72px', zIndex: 30, padding: '6px', borderRadius: '12px',
        background: C.card, border: `1px solid ${C.strokeHi}`, boxShadow: '0 12px 32px rgba(0,0,0,.45)',
        display: 'flex', flexDirection: 'column', gap: '2px'
      })
    },
      menuItem('prefs', '⚙', s.prefMenuPrefs, '', () => actions.openSettings(), C.text, C.muted),
      user.isAdmin && menuItem('admin', '◆', s.prefMenuAdmin, t.admin.sidebar, actions.openAdmin, C.accentHi, C.accent),
      menuItem('signout', '↩', s.prefMenuSignOut, '', actions.signOut, C.textDim, C.muted)
    ),

    h('div', {
      key: 'card',
      style: css({
        position: 'relative', zIndex: 31, display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', borderRadius: '12px', background: C.card,
        border: `1px solid ${state.accountMenuOpen ? C.accent : C.line}`
      })
    },
      h('div', {
        ...tap(() => actions.toggleAccountMenu(), s.prefAccountMenu),
        'aria-haspopup': 'menu', 'aria-expanded': String(Boolean(state.accountMenuOpen)),
        style: css({ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' })
      },
        h('div', {
          style: css({
            position: 'relative', width: '34px', height: '34px', flex: '0 0 34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: '50%', background: C.line, border: `1px solid ${C.strokeHi}`, fontSize: '14px', fontWeight: 700, color: C.accentHi
          })
        },
          initial,
          h('span', {
            class: busy ? 'pt-pulse' : null,
            title: state.online ? s.synced : s.offline,
            style: css({ position: 'absolute', right: '-1px', bottom: '-1px', width: '10px', height: '10px', borderRadius: '50%', background: dot, border: `2px solid ${C.card}` })
          })
        ),
        h('div', { style: css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }) },
          h('span', { style: css({ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, name),
          h('span', { style: css({ fontFamily: MONO, fontSize: '10.5px', color: !state.online ? C.danger : C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, line)
        )
      ),
      h('div', {
        ...tap(() => actions.openSettings(), s.prefMenuPrefs),
        title: s.prefMenuPrefs,
        class: 'pt-button',
        style: css({
          width: '36px', height: '36px', flex: '0 0 36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '9px', border: `1px solid ${C.stroke}`, color: C.textDim, fontSize: '15px', cursor: 'pointer'
        })
      }, '⚙')
    )
  );
}

// ---------------------------------------------------------------- header ----

function header({ state, metrics, t, fmt, actions }) {
  return h('div', {
    style: css({
      height: '74px', flex: '0 0 74px', borderBottom: `1px solid ${C.line}`,
      background: C.panel, display: 'flex', alignItems: 'center', gap: '16px',
      padding: '0 24px', position: 'sticky', top: 0, zIndex: 5
    })
  },
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }) },
      kicker(t.pages[state.tab][0], { fontSize: '9px', letterSpacing: '1.4px' }),
      h('div', {
        style: css({ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.4px', whiteSpace: 'nowrap' })
      }, t.pages[state.tab][1])
    ),
    h('div', { style: css({ flex: 1 }) }),
    h('div', {
      style: css({
        display: 'flex', alignItems: 'center', gap: '4px',
        border: `1px solid ${C.stroke}`, borderRadius: '7px', background: C.card, padding: '4px'
      })
    },
      stepButton('‹', () => actions.stepMonth(-1), t.shell.ariaPrevMonth),
      h('div', {
        style: css({
          fontFamily: MONO, fontSize: '11.5px', letterSpacing: '1px',
          minWidth: '88px', textAlign: 'center', color: C.text
        })
      }, fmt.monthName(state.month).toUpperCase()),
      stepButton('›', () => actions.stepMonth(1), t.shell.ariaNextMonth)
    ),
    h('div', {
      style: css({
        display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px',
        paddingLeft: '14px', borderLeft: `1px solid ${C.line}`
      })
    },
      kicker(t.netWorth, { fontSize: '9px', letterSpacing: '1.2px' }),
      h('div', {
        style: css({ fontFamily: MONO, fontSize: '17px', fontWeight: 700, color: netColor(metrics.totals.netWorth) })
      }, fmt.money(metrics.totals.netWorth))
    )
  );
}

const stepButton = (glyph, onClick, label) => h('div', {
  ...tap(onClick, label),
  class: 'pt-step',
  style: css({
    width: '26px', height: '26px', display: 'flex', alignItems: 'center',
    justifyContent: 'center', borderRadius: '5px', cursor: 'pointer',
    color: C.muted, fontFamily: MONO, userSelect: 'none'
  })
}, glyph);

// -------------------------------------------------------------- overview ----

function overview({ metrics, t, fmt, state, actions, inputs }) {
  /* The swap between the overview and its simulator slides, once per swap (see ptSubIn). */
  const motion = state.whatIfFrom === 1 ? 'pt-sub-in' : state.whatIfFrom === -1 ? 'pt-sub-back' : null;
  if (state.whatIfOpen) {
    return h('div', { key: 'simulator', class: motion },
      simulatorPage({ t, actions }, whatIfPanel({ state, metrics, t, fmt, actions })));
  }
  const ctx = { metrics, t, fmt, state, actions, inputs };

  /*
   * Rows that wrap rather than a fixed grid: each card says how wide it would
   * like to be, and on a narrower window the second one of a row simply drops
   * under the first instead of both being squeezed.
   */
  const row = (...cells) => {
    const shown = cells.filter(([node]) => node);
    if (!shown.length) return null;
    return h('div', { style: css({ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'stretch' }) },
      ...shown.map(([node, flex], i) => h('div', {
        key: 'c' + i,
        style: css({ flex, minWidth: 0, display: 'flex', flexDirection: 'column' })
      }, node)));
  };

  return h('div', { key: 'dashboard', class: motion, style: css({ display: 'flex', flexDirection: 'column', gap: '16px' }) },
    termsNotice({ state, t, actions }),
    kpiRow(ctx),
    row([trendCard(ctx), '3 1 600px'], [moneyCard(ctx), '1 1 300px']),
    row([categoriesCard(ctx), '1 1 460px'], [attentionCard(ctx), '1 1 380px']),
    goalsCard(ctx),
    /*
     * The one panel here that looks forward: what is about to go out. Every
     * fixed cost carries a cadence and usually the day it lands on.
     */
    upcomingCard(ctx),
    row([reviewCard(ctx), '3 1 560px'], [whatIfCard(ctx), '1 1 300px']),
    /*
     * The only advertising slot in the app, and the last thing on the page.
     *
     * Never on a screen where somebody is entering money, importing a statement
     * or typing an API key. Returns null for a paid account, for an instance
     * with no publisher id, and until the consent question has been answered.
     */
    adSlot({ state, t, actions })
  );
}

const metricCard = (
  label, value, { sub, subColor = C.muted, valueColor = C.text, note = null } = {}
) =>
  card({ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '9px' },
    kicker(label),
    h('div', {
      style: css({ fontSize: '26px', fontWeight: 700, letterSpacing: '-0.8px', color: valueColor })
    }, value),
    sub && h('div', { style: css({ fontFamily: MONO, fontSize: '10.5px', color: subColor }) }, sub),
    /*
     * A second line, under the comparison rather than instead of it.
     *
     * The month-on-month figure is what the tile is for; anything standing
     * beside the number has to be added without taking that away.
     */
    note && h('div', {
      style: css({ fontSize: '10px', color: C.ghost, lineHeight: 1.4 })
    }, note)
  );

const splitRow = (label, value, valueColor = C.text) => h('div', {
  style: css({
    display: 'flex', justifyContent: 'space-between', fontFamily: MONO,
    fontSize: '11px', color: C.faint
  })
}, h('span', null, label), h('span', { style: css({ color: valueColor }) }, value));

// -------------------------------------------------------------- cashflow ----

function cashflow({ metrics, t, fmt, state, inputs, actions }) {
  const ctx = { metrics, t, fmt, state, inputs, actions };
  /*
   * The design's order: the month in four figures, where the income went, and
   * then the transactions beside the fixed costs that feed them -- each half
   * as wide as it would like, the second dropping under the first when the
   * window is narrow.
   */
  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '16px' }) },
    cashKpis(ctx),
    flowCard(ctx),
    budgetsCard(ctx),
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'stretch' }) },
      h('div', { style: css({ flex: '1 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column' }) }, transactionsCard(ctx)),
      h('div', { style: css({ flex: '1 1 460px', minWidth: 0, display: 'flex', flexDirection: 'column' }) }, fixedCard(ctx))
    )
  );
}

const rowStyle = css({
  display: 'flex', alignItems: 'center', gap: '12px',
  padding: '12px 18px', borderBottom: `1px solid ${C.lineSoft}`
});

const smallCard = (label, value, color = C.text) =>
  card({ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '8px' },
    kicker(label),
    h('div', {
      style: css({ fontSize: '24px', fontWeight: 700, letterSpacing: '-0.7px', color })
    }, value)
  );

const listHeader = (title, note) => h('div', {
  style: css({
    padding: '16px 18px', display: 'flex', alignItems: 'baseline',
    gap: '10px', borderBottom: `1px solid ${C.line}`
  })
},
  h('div', { style: css({ fontSize: '14px', fontWeight: 700 }) }, title),
  note && h('div', { style: css({ fontFamily: MONO, fontSize: '10px', color: C.faint }) }, note)
);

// ------------------------------------------------------------------ debt ----

function debtTab({ metrics, t, fmt, state, inputs, actions }) {
  /* One plan for the figures, the chart and the plan card: they must agree. */
  const plans = debtPlans(metrics, state);
  const ctx = { metrics, t, fmt, state, inputs, actions, plans };
  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '16px' }) },
    debtKpis(ctx),
    debtChart(ctx),
    debtQueue(ctx),
    debtPlanCard(ctx)
  );
}

// ----------------------------------------------------------- investments ----

function investments(ctx) {
  return investTab({ ...ctx, compact: false });
}

const cell = (value, color, size = '12px') => h('div', {
  style: css({ textAlign: 'right', fontFamily: MONO, fontSize: size, color })
}, value);

// --------------------------------------------------------------- vehicle ----

function vehicle(ctx) {
  /* The car's own details open in place, under the strip whose ✎ opened them. */
  const editor = ctx.state.sheet === 'vehicle' ? vehicleForm(ctx) : null;
  return vehicleTab({ ...ctx, compact: false }, editor);
}

